/**
 * ============================================================
 * KEJJA OS — src/pages/finance/CreditPage.tsx  (v1.1)
 * Fuliza, M-Shwari, Okoa Jahazi, Tala — limit & used tracking
 * ============================================================
 */

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';

import { useFinanceContext } from './FinanceLayout';
import {
  useFinanceCreditLines, useCreateCreditLine, useUpdateCreditLine, useDeleteCreditLine,
} from '@/hooks/useFinance';
import type { CreditKind } from '@/lib/financeApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatMoney } from '@/lib/formatMoney';
import { cn } from '@/lib/utils';

const KIND_OPTIONS: Array<{ value: CreditKind; label: string }> = [
  { value: 'fuliza', label: 'Fuliza' },
  { value: 'mshwari', label: 'M-Shwari' },
  { value: 'okoa_jahazi', label: 'Okoa Jahazi' },
  { value: 'kcb_mpesa', label: 'KCB M-Pesa' },
  { value: 'tala', label: 'Tala' },
  { value: 'branch', label: 'Branch' },
  { value: 'sacco_loan', label: 'SACCO Loan' },
  { value: 'bank_overdraft', label: 'Bank Overdraft' },
  { value: 'other', label: 'Other' },
];

export default function CreditPage() {
  const { homeId } = useFinanceContext();
  const { data, isLoading } = useFinanceCreditLines(homeId);
  const create = useCreateCreditLine(homeId);
  const update = useUpdateCreditLine(homeId);
  const remove = useDeleteCreditLine(homeId);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    kind: 'fuliza' as CreditKind,
    name: 'Fuliza',
    limitAmount: '',
    usedAmount: '',
  });

  const [editLimitFor, setEditLimitFor] = useState<string | null>(null);
  const [editLimitVal, setEditLimitVal] = useState('');

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    create.mutate({
      kind: form.kind,
      name: form.name.trim(),
      limitAmount: form.limitAmount ? Math.round(parseFloat(form.limitAmount) * 100) : 0,
      usedAmount: form.usedAmount ? Math.round(parseFloat(form.usedAmount) * 100) : 0,
    }, {
      onSuccess: () => {
        setShowAdd(false);
        setForm({ kind: 'fuliza', name: 'Fuliza', limitAmount: '', usedAmount: '' });
      },
    });
  }

  function saveLimit(creditId: string) {
    const minor = editLimitVal ? Math.round(parseFloat(editLimitVal) * 100) : 0;
    update.mutate({ creditId, patch: { limitAmount: minor } }, {
      onSuccess: () => { setEditLimitFor(null); setEditLimitVal(''); },
    });
  }

  return (
    <div className="space-y-5 pb-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Credit lines</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Fuliza, M-Shwari, Okoa — how much is riding
          </p>
        </div>
        <Button
          size="sm"
          className="bg-orange-600 hover:bg-orange-700 text-white"
          onClick={() => setShowAdd(!showAdd)}
        >
          <Plus className="w-4 h-4 mr-1" /> Add line
        </Button>
      </div>

      {showAdd && (
        <Card className="border-orange-200 bg-orange-50/30 dark:bg-orange-950/10">
          <CardContent className="p-4">
            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <Label className="text-xs block mb-1.5">Kind</Label>
                <div className="grid grid-cols-3 gap-1">
                  {KIND_OPTIONS.map((k) => (
                    <button
                      key={k.value} type="button"
                      onClick={() => setForm({ ...form, kind: k.value, name: k.label })}
                      className={cn(
                        'px-2 py-1.5 text-[11px] rounded-lg border font-medium',
                        form.kind === k.value
                          ? 'bg-orange-600 text-white border-orange-600'
                          : 'border-border bg-background hover:bg-muted',
                      )}
                    >
                      {k.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="credit-name" className="text-xs">Display name</Label>
                <Input
                  id="credit-name"
                  value={form.name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="credit-limit" className="text-xs">Limit (KSh)</Label>
                  <Input
                    id="credit-limit" type="number" step="0.01"
                    placeholder="e.g. 5000"
                    value={form.limitAmount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, limitAmount: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="credit-used" className="text-xs">Currently used</Label>
                  <Input
                    id="credit-used" type="number" step="0.01"
                    placeholder="0"
                    value={form.usedAmount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, usedAmount: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={create.isPending} className="bg-orange-600 hover:bg-orange-700 text-white">
                  {create.isPending ? 'Adding…' : 'Add credit line'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Skeleton className="h-24 rounded-xl" />
      ) : !data || data.creditLines.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No credit lines tracked yet. Add Fuliza or M-Shwari to see your real exposure.
              <br />
              <span className="text-[11px]">Fuliza usage gets auto-tracked when you import M-Pesa SMS.</span>
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.creditLines.map((c) => {
            const pct = c.limitAmount > 0 ? Math.round((c.usedAmount / c.limitAmount) * 100) : 0;
            const high = pct > 80;
            const mid = pct > 50;
            return (
              <Card key={c.id} className={cn(
                c.usedAmount > 0 ? 'border-rose-200 dark:border-rose-900/40' : '',
              )}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm">{c.name}</h3>
                        <Badge variant="outline" className="text-[9px] uppercase font-normal">
                          {c.kind.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600 flex-shrink-0"
                      onClick={() => { if (confirm(`Delete "${c.name}"?`)) remove.mutate(c.id); }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  <div className="mt-3">
                    <div className="flex items-baseline justify-between mb-1">
                      <div>
                        <span className={cn(
                          'text-xl font-bold tabular-nums',
                          c.usedAmount > 0 ? 'text-rose-600' : 'text-muted-foreground',
                        )}>
                          {formatMoney(c.usedAmount, c.currencyCode)}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">outstanding</span>
                      </div>
                      {editLimitFor === c.id ? (
                        <div className="flex gap-1 items-center">
                          <Input
                            type="number" step="0.01" autoFocus
                            value={editLimitVal}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEditLimitVal(e.target.value)}
                            className="h-7 w-24 text-sm"
                          />
                          <Button size="sm" className="h-7" onClick={() => saveLimit(c.id)}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditLimitFor(null)}>×</Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setEditLimitFor(c.id); setEditLimitVal((c.limitAmount / 100).toString()); }}
                          className="text-xs text-muted-foreground hover:text-foreground tabular-nums hover:underline"
                        >
                          of {formatMoney(c.limitAmount, c.currencyCode)} limit ✎
                        </button>
                      )}
                    </div>
                    {c.limitAmount > 0 && (
                      <>
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full transition-all',
                              high ? 'bg-rose-500' : mid ? 'bg-amber-500' : 'bg-emerald-500',
                            )}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">{pct}% used</div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
